import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, homeForRole } from "../lib/auth";
import { TID } from "../constants/testIds";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck } from "lucide-react";
import Logo from "../components/Logo";
import RotatingTagline from "../components/RotatingTagline";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success(`Welcome back, ${data.user.name}`);
      nav(homeForRole(data.user.role));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const fill = (e, p) => { setEmail(e); setPassword(p); };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left visual panel , brand dark */}
      <div
        className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden on-dark"
        style={{ background: "linear-gradient(135deg, #1A1743 0%, #373281 55%, #221D53 100%)" }}
      >
        {/* Soft accent orange glow */}
        <div className="absolute -bottom-32 -right-32 w-[520px] h-[520px] rounded-full opacity-30 pointer-events-none blur-3xl"
             style={{ background: "radial-gradient(circle, #E4943A 0%, transparent 70%)" }}/>
        <div className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full opacity-20 pointer-events-none blur-3xl"
             style={{ background: "radial-gradient(circle, #FFFFFF 0%, transparent 70%)" }}/>

        <div className="relative z-10">
          <Logo variant="light" size={40} testid="login-brand-logo"/>
        </div>

        <div className="relative z-10 text-white max-w-xl">
          <div className="text-[10px] font-mono tracking-widest uppercase mb-4" style={{ color: "#E4943A" }}>
            Employee Relations
          </div>
          <RotatingTagline testid="login-tagline"/>
          <div className="mt-8 flex flex-wrap gap-4 text-xs font-mono uppercase tracking-widest" style={{ color: "#E4943A" }}>
            <span className="inline-flex items-center gap-1.5" style={{ color: "rgba(255,255,255,0.7)" }}><ShieldCheck size={12} strokeWidth={2} style={{ color: "#E4943A" }}/> SOC 2</span>
            <span className="inline-flex items-center gap-1.5" style={{ color: "rgba(255,255,255,0.7)" }}><ShieldCheck size={12} strokeWidth={2} style={{ color: "#E4943A" }}/> GDPR</span>
            <span className="inline-flex items-center gap-1.5" style={{ color: "rgba(255,255,255,0.7)" }}><ShieldCheck size={12} strokeWidth={2} style={{ color: "#E4943A" }}/> CCPA</span>
            <span className="inline-flex items-center gap-1.5" style={{ color: "rgba(255,255,255,0.7)" }}><ShieldCheck size={12} strokeWidth={2} style={{ color: "#E4943A" }}/> EEOC</span>
          </div>
        </div>

        <div className="relative z-10 text-[10px] font-mono tracking-widest uppercase text-[color:var(--fg-on-dark-muted)]">
          © 2026 PropHR, Inc.
        </div>
      </div>

      {/* Right form panel , light */}
      <div className="flex items-center justify-center px-6 py-12 bg-white on-light">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden mb-8">
            <Logo variant="dark" size={32}/>
          </div>

          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-3">Sign in</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-slate-900 leading-tight">
            Welcome back.
          </h1>
          <p className="text-slate-500 mt-3 text-sm">Access your PropHR workspace with your credentials.</p>

          <form className="mt-8 space-y-4" onSubmit={submit}>
            <div>
              <label className="eyebrow block mb-2">Work email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-slate-300 rounded px-3 py-2.5 text-sm bg-white text-slate-900 focus:border-brand focus:outline-none focus:ring-2 ring-brand"
                style={{ boxShadow: "none" }}
                placeholder="you@company.com"
                data-testid={TID.loginEmail}
              />
            </div>
            <div>
              <label className="eyebrow block mb-2">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-slate-300 rounded px-3 py-2.5 text-sm bg-white text-slate-900 focus:border-brand focus:outline-none focus:ring-2 ring-brand"
                placeholder="Enter your password"
                data-testid={TID.loginPassword}
              />
            </div>
            <button className="btn-primary w-full py-3" disabled={loading} data-testid={TID.loginSubmit}>
              {loading ? "Signing in…" : "Sign in"} <ArrowRight size={14} strokeWidth={2}/>
            </button>
          </form>

          {/* SSO placeholders */}
          <div className="mt-6 grid grid-cols-2 gap-3">
            <button className="btn-ghost text-xs" onClick={() => toast.info("Google SSO coming soon")}>
              Google Workspace
            </button>
            <button className="btn-ghost text-xs" onClick={() => toast.info("Microsoft 365 SSO coming soon")}>
              Microsoft 365
            </button>
          </div>

          <div className="mt-8 border-t border-slate-200 pt-6">
            <div className="eyebrow mb-3">Try demo accounts</div>
            <div className="grid grid-cols-1 gap-2 text-xs font-mono">
              {[
                ["Super Admin", "superadmin@prophr.com", "SuperAdmin@123"],
                ["Company Admin", "admin@acme.com", "CompanyAdmin@123"],
                ["HR", "hr@acme.com", "Hr@123"],
                ["Manager", "manager@acme.com", "Manager@123"],
                ["Employee", "employee@acme.com", "Employee@123"],
              ].map(([role, e, p]) => (
                <button
                  key={e}
                  data-testid={`demo-login-${role.toLowerCase().replaceAll(" ", "-")}`}
                  className="flex items-center justify-between border border-slate-200 rounded px-3 py-2 hover:border-brand text-left transition-colors"
                  onClick={() => fill(e, p)}
                >
                  <span className="text-slate-500">{role}</span>
                  <span className="text-slate-900">{e}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6 text-sm text-slate-500 text-center">
            New to PropHR?{" "}
            <Link to="/register" className="text-brand hover:underline font-semibold" data-testid={TID.loginRegisterLink}>
              Create an account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
